import type {
  ActionEnvelope,
  ChatState,
  ChatSummary,
  SessionAction,
  SessionState,
  ToolCallState,
} from '@microsoft/agent-host-protocol'
import {sessionReducer} from '@microsoft/agent-host-protocol'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {resolveSubagent, type SubagentHandle, type SubagentResolver} from '@github-ui/ahp-relay/ahp-session-selectors'
import {Reassembler, type ChunkEnvelope} from '@github-ui/ahp-relay/webpubsub/chunking'

const IDLE_STATUS = 1 as ChatState['status']
const READY_LIFECYCLE = 'ready' as SessionState['lifecycle']
const CATALOG_ACTIONS = new Set(['session/chatAdded', 'session/chatUpdated', 'session/chatRemoved'])

export interface AhpReplicationFrame {
  readonly ns?: string
  readonly seq?: number
  readonly at?: string
  readonly session_id?: string
  readonly payload?: ChunkEnvelope
}

export interface AhpSessionBundle {
  sessionState: SessionState
  chatStates: Readonly<Record<string, ChatState>>
  chatResourcesWithPersistedActions: Readonly<Record<string, true>>
  defaultChatState: ChatState | undefined
  catalogIsAuthoritative: boolean
}

function isActionEnvelope(value: unknown): value is ActionEnvelope {
  if (value === null || typeof value !== 'object') return false
  const env = value as Partial<ActionEnvelope>
  return (
    typeof env.channel === 'string' &&
    typeof env.serverSeq === 'number' &&
    !!env.action &&
    typeof env.action.type === 'string'
  )
}

function reassembleEnvelopes(frames: readonly AhpReplicationFrame[]): ActionEnvelope[] {
  const reassembler = new Reassembler()
  const envelopes: ActionEnvelope[] = []
  const ordered = [...frames].sort((a, b) => (a?.seq ?? 0) - (b?.seq ?? 0))
  for (const frame of ordered) {
    if (!frame?.payload) continue
    let payload: unknown
    try {
      payload = reassembler.ingest(frame.payload)
    } catch {
      continue
    }
    if (payload == null) continue
    if (isActionEnvelope(payload) && !payload.rejectionReason) envelopes.push(payload)
  }
  return envelopes
}

function groupChatEnvelopes(envelopes: readonly ActionEnvelope[]): Map<string, ActionEnvelope[]> {
  const byChannel = new Map<string, ActionEnvelope[]>()
  for (const env of envelopes) {
    if (!env.action.type.startsWith('chat/')) continue
    const existing = byChannel.get(env.channel)
    if (existing) {
      existing.push(env)
    } else {
      byChannel.set(env.channel, [env])
    }
  }
  return byChannel
}

function selectDefaultChat(
  chatStates: Readonly<Record<string, ChatState>>,
  byChannel: ReadonlyMap<string, ActionEnvelope[]>,
  preferredChannels: ReadonlyArray<string | undefined>,
  allowedChannels?: ReadonlySet<string>,
): ChatState | undefined {
  for (const channel of preferredChannels) {
    if (!channel || (allowedChannels && !allowedChannels.has(channel))) continue
    const state = chatStates[channel]
    if (state) return state
  }

  let best: string | undefined
  let bestCount = -1
  for (const [channel, envs] of byChannel) {
    if (allowedChannels && !allowedChannels.has(channel)) continue
    if (envs.length > bestCount) {
      best = channel
      bestCount = envs.length
    }
  }
  return best ? chatStates[best] : undefined
}

export interface RebuildOptions {
  sessionChannel?: string
  chatChannel: string
  title?: string
  modifiedAt?: string
}

function createSessionSeed(title: string): SessionState {
  return {
    provider: '',
    title,
    status: IDLE_STATUS,
    lifecycle: READY_LIFECYCLE,
    activeClients: [],
    chats: [],
  }
}

function createChatSeed(
  channel: string,
  summary: ChatSummary | undefined,
  title: string,
  modifiedAt: string,
): ChatState {
  return {
    resource: channel,
    title: summary?.title ?? title,
    status: summary?.status ?? IDLE_STATUS,
    modifiedAt: summary?.modifiedAt ?? modifiedAt,
    turns: [],
    ...(summary?.activity ? {activity: summary.activity} : {}),
    ...(summary?.origin ? {origin: summary.origin} : {}),
    ...(summary?.interactivity ? {interactivity: summary.interactivity} : {}),
    ...(summary?.workingDirectories ? {workingDirectories: summary.workingDirectories} : {}),
  }
}

function foldChatState(seed: ChatState, envelopes: readonly ActionEnvelope[]): ChatState {
  const store = new AhpChatStore()
  store.start()
  store.applySnapshot(seed)
  for (const envelope of [...envelopes].sort((a, b) => a.serverSeq - b.serverSeq)) {
    store.applyAction(envelope)
  }
  return store.getSnapshot() ?? seed
}

export function rebuildAhpSessionBundleFromEvents(
  frames: readonly AhpReplicationFrame[],
  options: RebuildOptions,
): AhpSessionBundle | undefined {
  const {chatChannel, title = '', modifiedAt = ''} = options
  const sessionChannel = options.sessionChannel ?? chatChannel.replace(/\/chat(?:\/.*)?$/, '')
  const envelopes = reassembleEnvelopes(frames)
  if (envelopes.length === 0) return undefined

  let sessionState = createSessionSeed(title)
  let catalogIsAuthoritative = false
  const removedChatResources = new Set<string>()
  const sessionEnvelopes = envelopes
    .filter(envelope => envelope.channel === sessionChannel && envelope.action.type.startsWith('session/'))
    .sort((a, b) => a.serverSeq - b.serverSeq)

  for (const envelope of sessionEnvelopes) {
    const action = envelope.action as SessionAction
    if (CATALOG_ACTIONS.has(action.type)) catalogIsAuthoritative = true
    if (action.type === 'session/chatAdded') {
      removedChatResources.delete(action.summary.resource)
    } else if (action.type === 'session/chatRemoved') {
      removedChatResources.add(action.chat)
    }
    sessionState = sessionReducer(sessionState, action)
  }

  const byChannel = groupChatEnvelopes(envelopes)
  const chatResourcesWithPersistedActions: Record<string, true> = {}
  for (const channel of byChannel.keys()) chatResourcesWithPersistedActions[channel] = true
  for (const summary of sessionState.chats) {
    if (!byChannel.has(summary.resource)) byChannel.set(summary.resource, [])
  }

  const latestModifiedAt = [...frames].reverse().find(frame => frame.at)?.at ?? modifiedAt
  const summaries = new Map(sessionState.chats.map(summary => [summary.resource, summary]))
  const catalogResources = new Set(summaries.keys())
  if ((byChannel.get(chatChannel)?.length ?? 0) > 0 && !removedChatResources.has(chatChannel)) {
    catalogResources.add(chatChannel)
  }
  const chatStates: Record<string, ChatState> = {}
  for (const [channel, chatEnvelopes] of byChannel) {
    const summary = summaries.get(channel)
    const seedTitle = channel === chatChannel ? sessionState.title || title : (summary?.title ?? channel)
    chatStates[channel] = foldChatState(createChatSeed(channel, summary, seedTitle, latestModifiedAt), chatEnvelopes)
  }

  return {
    sessionState,
    chatStates,
    chatResourcesWithPersistedActions,
    defaultChatState: selectDefaultChat(
      chatStates,
      byChannel,
      [sessionState.defaultChat, chatChannel],
      catalogIsAuthoritative ? catalogResources : undefined,
    ),
    catalogIsAuthoritative,
  }
}

export function rebuildChatStateFromEvents(
  frames: readonly AhpReplicationFrame[],
  options: RebuildOptions,
): ChatState | undefined {
  return rebuildAhpSessionBundleFromEvents(frames, options)?.defaultChatState
}

export function resolvePersistedSubagent(
  bundle: AhpSessionBundle,
  toolCall: ToolCallState,
  parentChatResource: string | undefined,
): SubagentHandle | undefined {
  return resolveSubagent(toolCall, bundle.sessionState.chats, parentChatResource, bundle.catalogIsAuthoritative)
}

export const createPersistedSubagentResolver =
  (bundle: AhpSessionBundle): SubagentResolver =>
  (toolCall, parentChatResource) =>
    resolvePersistedSubagent(bundle, toolCall, parentChatResource)
